import { PASTE_SETTLE_DELAY_MS } from "../constants";
import { isEntityPaste } from "../content-manager";
import {
  blockHtmlToInline,
  cleanPastedHtml,
  extractInternalContent,
  isInternalStorydocPaste,
  pastedHtmlToContent,
} from "../paste-cleaner";
import type { FroalaEditorInstance } from "../types";

/**
 * True when the caret sits inside a list item. Froala v5's `html.insert` drops
 * block-level content (`<p>`, headings) pasted into a list item whose caret is
 * nested in a child element — the normal Storydoc structure, where bullet text
 * lives inside styling / highlight / variable `<span>`s. It unwraps the block to
 * a leading text node, then feeds that string to `querySelectorAll` as a
 * selector and throws, leaving nothing inserted. Inserting inline content
 * instead sidesteps the broken path. See Monday #12279122681.
 */
const isCaretInListItem = (editor: FroalaEditorInstance): boolean => {
  try {
    const el = editor.selection.element();
    return Boolean(el && typeof el.closest === "function" && el.closest("li"));
  } catch {
    return false;
  }
};

/**
 * A pasted `{{token}}` must become an atom, not literal text. It arrives that
 * way from a plain-text clipboard, and also from any HTML clipboard whose pill
 * markup did not survive the trip (another app, an editor that drops unknown
 * attributes). Cheap substring probe — the DOM re-hydration itself is the
 * authority on what is a real token.
 */
const hasVariableToken = (text: string): boolean => text.includes("{{");

/** Escape plain-text clipboard data and turn newlines into <br> for inline insert. */
const plainTextToInlineHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .split(/\r\n|\r|\n/)
    .join("<br>");

/**
 * `rehydratePills` turns `{{token}}` text the paste just inserted back into
 * pill atoms (and re-guards the ones that came in as markup). It runs on the
 * live editable straight after every insert this handler performs, before the
 * debounced save serializes the content back to tokens. Optional so callers
 * that don't render pills (tests, non-variable editors) stay unchanged.
 */
export const createPasteBeforeHandler = (
  rehydratePills?: (root: HTMLElement) => void,
) => {
  return function (this: FroalaEditorInstance, e: ClipboardEvent) {
    if (!e || !e.clipboardData) return;
    if (isEntityPaste(e)) return e.preventDefault();

    const rehydrate = () => {
      const root = this.$el?.[0];
      if (root && rehydratePills) rehydratePills(root);
    };

    const inList = isCaretInListItem(this);
    const html = e.clipboardData.getData("text/html");

    // Plain-text paste (no HTML on the clipboard). Outside a list we defer to
    // Froala's native paste. Inside a list we must insert inline ourselves —
    // Froala wraps the text in a block and hits the list-insert bug above,
    // silently dropping the paste.
    if (!html.trim()) {
      const plain =
        e.clipboardData.getData("text/plain") ||
        e.clipboardData.getData("text");
      // A token needs the manual insert too — Froala's native paste drops the
      // text in without ever giving us a hook to re-hydrate it into a pill.
      if (!inList && !hasVariableToken(plain)) return;
      if (!plain) return;
      try {
        this.html.insert(plainTextToInlineHtml(plain));
      } catch {
        // Froala internal DOM error — content may have been partially inserted
      }
      rehydrate();
      setTimeout(() => {
        this.save.save();
      }, PASTE_SETTLE_DELAY_MS);
      return false;
    }

    // Internal copy from another Storydoc text field: keep everything
    // (icons, highlights, RKPI, animations, design-system fonts/colors).
    // Strip the layout wrappers and insert manually so we bypass Froala's
    // pasteDeniedTags ("svg", "img") — otherwise SVG-rendered icons would
    // be stripped before they ever reach the destination editor.
    if (isInternalStorydocPaste(html)) {
      const inner = extractInternalContent(html);
      if (!inner) return false;
      try {
        this.html.insert(inList ? blockHtmlToInline(inner) : inner);
      } catch {
        // Froala internal DOM error — content may have been partially inserted
      }
      rehydrate();
      setTimeout(() => {
        this.save.save();
      }, PASTE_SETTLE_DELAY_MS);
      return false;
    }

    const cleaned = cleanPastedHtml(html);
    if (!cleaned) {
      // The clipboard HTML had no textual content once media was stripped —
      // e.g. desktop PowerPoint copies TEXT as a rendered picture and provides
      // image-only HTML. Fall back to the plain-text flavor so the text pastes
      // as text instead of nothing (and never as the image). Bug 11445195655.
      const plain =
        e.clipboardData.getData("text/plain") ||
        e.clipboardData.getData("text");
      if (!plain.trim()) return false;
      try {
        this.html.insert(plainTextToInlineHtml(plain));
      } catch {
        // Froala internal DOM error — content may have been partially inserted
      }
      rehydrate();
      setTimeout(() => {
        this.save.save();
      }, PASTE_SETTLE_DELAY_MS);
      return false;
    }

    try {
      this.html.insert(inList ? blockHtmlToInline(cleaned) : cleaned);
    } catch {
      // Froala internal DOM error — content may have been partially inserted
    }
    rehydrate();
    setTimeout(() => {
      this.save.save();
    }, PASTE_SETTLE_DELAY_MS);
    return false;
  };
};

export const createPasteAfterCleanupHandler = () => {
  // Defensive net: if Froala ever fires afterCleanup despite our paste.before
  // returning false, fall back to the same routing.
  return (_html: string) => pastedHtmlToContent(_html);
};
